import { useMemo } from "react";
import { useFormik } from "formik";
import { Flex, FormControl, FormLabel, Select as ChakraSelect, Input as ChakraInput, Button } from "@chakra-ui/react";
import { RootState, useAppSelector } from "@/store";
import { ProjectsFilter } from "../_helpers";
import { Recruiter } from "@/model/recruiters";

export interface ProjectsPopupFilterProps {
  readonly values: ProjectsFilter;
  readonly onApply: (values: ProjectsFilter) => void;
  readonly onReset: () => void;
  readonly customerOptions: readonly { value: string; label: string }[];
  readonly projectStatusOptions: readonly { value: string; label: string }[];
  readonly legalEntityOptions: readonly { value: string; label: string }[];
  readonly recruiters: readonly Recruiter[];
}

export const ProjectsPopupFilter = ({
  values,
  onApply,
  onReset,
  customerOptions,
  projectStatusOptions,
  legalEntityOptions,
  recruiters,
}: ProjectsPopupFilterProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const formik = useFormik<ProjectsFilter>({
    initialValues: values,
    enableReinitialize: true,
    onSubmit: (submittedValues: ProjectsFilter) => {
      onApply(submittedValues);
    },
  });

  const recruiterOptions = useMemo(() => recruiters, [recruiters]);

  const handleResetLocal = () => {
    onReset();
  };

  const handleApplyLocal = () => {
    void formik.submitForm();
  };

  return (
    <>
      <FormControl>
        <FormLabel>Заказчик</FormLabel>
        <ChakraSelect
          placeholder="Выберите заказчика"
          value={formik.values.customer_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("customer_id", nextValue || undefined);
          }}
        >
          {customerOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Статус проекта</FormLabel>
        <ChakraSelect
          placeholder="Выберите статус"
          value={formik.values.status || ""}
          onChange={(e) => {
            formik.setFieldValue("status", e.target.value || "");
          }}
        >
          {projectStatusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Юр. лицо</FormLabel>
        <ChakraSelect
          placeholder="Выберите юр. лицо"
          value={formik.values.legal_entity_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("legal_entity_id", nextValue || undefined);
          }}
        >
          {legalEntityOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Аккаунт-менеджер</FormLabel>
        <ChakraSelect
          placeholder="Выберите менеджера"
          value={formik.values.responsible_id ?? ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("responsible_id", nextValue || undefined);
          }}
        >
          {recruiterOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.username}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Дата создания</FormLabel>
        <Flex direction="row" gap="12px">
          <ChakraInput
            type="date"
            value={formik.values.created_at_from || ""}
            placeholder="От"
            onChange={(e) => formik.setFieldValue("created_at_from", e.target.value || "")}
          />
          <ChakraInput
            type="date"
            value={formik.values.created_at_to || ""}
            placeholder="До"
            onChange={(e) => formik.setFieldValue("created_at_to", e.target.value || "")}
          />
        </Flex>
      </FormControl>
      <Flex justifyContent="flex-end" gap="8px">
        <Button variant="ghost" onClick={handleResetLocal}>
          Сбросить
        </Button>
        <Button onClick={handleApplyLocal} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
          Применить
        </Button>
      </Flex>
    </>
  );
};
